import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Wizard de dominio en lenguaje sencillo (WEB-08 / WEB-16).
 * Detalles técnicos solo en sección plegable.
 */
export default function WebDomains() {
  const { siteId } = useParams();
  const [domains, setDomains] = useState([]);
  const [hostname, setHostname] = useState('');
  const [mode, setMode] = useState('custom'); // custom | platform
  const [advancedCname, setAdvancedCname] = useState(false);
  const [step, setStep] = useState(1);

  const load = () => {
    api.get(`/tenant/web/sites/${siteId}/domains`)
      .then((r) => setDomains(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(load, [siteId]);

  const add = async () => {
    try {
      await api.post(`/tenant/web/sites/${siteId}/domains`, {
        hostname: hostname.trim(),
        domain_type: mode === 'platform' ? 'platform_subdomain' : 'custom',
        connection_mode: advancedCname ? 'cname' : 'nameserver',
      });
      toast.success(
        advancedCname
          ? 'Dominio registrado. Sigue los pasos en pantalla.'
          : 'Dominio registrado. Copia los 2 datos y pégalos donde compraste tu dominio.'
      );
      setHostname('');
      setStep(2);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const verify = async (id) => {
    try {
      await api.post(`/tenant/web/sites/${siteId}/domains/${id}/verify`);
      toast.success('Revisamos el estado de tu dominio');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Quitar este dominio del sitio?')) return;
    try {
      await api.delete(`/tenant/web/sites/${siteId}/domains/${id}`);
      toast.success('Dominio eliminado');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copiado');
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4" data-testid="web-domains">
      <Link to={`/tenant/web/sites/${siteId}`} className="text-xs text-[#F72585]">← Editor</Link>
      <h1 className="text-2xl font-bold">Conectar dirección web</h1>
      <p className="text-sm text-[#64748B]">
        Elige una dirección de CavernicolApp o la tuya (www.minegocio.com).
        No te pediremos la contraseña de donde compraste el dominio.
      </p>

      <div className="flex gap-2 text-sm">
        <button type="button" className={`px-3 py-1 rounded border ${mode === 'custom' ? 'bg-[#F72585] text-white' : ''}`} onClick={() => setMode('custom')}>Mi dominio</button>
        <button type="button" className={`px-3 py-1 rounded border ${mode === 'platform' ? 'bg-[#F72585] text-white' : ''}`} onClick={() => setMode('platform')}>Dirección CavernicolApp</button>
      </div>

      <ol className="text-xs text-[#64748B] list-decimal ml-5 space-y-1">
        <li className={step >= 1 ? 'text-[#0F172A] font-medium' : ''}>Elige tipo y escribe el nombre</li>
        <li className={step >= 2 ? 'text-[#0F172A] font-medium' : ''}>Sigue la guía de tu proveedor</li>
        <li className={step >= 2 ? 'text-[#0F172A] font-medium' : ''}>Pulsa «Verificar» cuando hayas guardado el cambio</li>
      </ol>

      <div className="flex gap-2">
        <Input
          placeholder={mode === 'custom' ? 'www.minegocio.com' : 'mi-negocio'}
          value={hostname}
          onChange={(e) => setHostname(e.target.value)}
        />
        <Button onClick={add}>Continuar</Button>
      </div>
      {mode === 'custom' && (
        <div className="text-xs bg-[#F0F9FF] border border-[#BAE6FD] rounded p-2 space-y-2">
          <p>
            Escribe tu dominio (ej. <strong>minegocio.com</strong>). Te daremos <strong>2 datos</strong> para
            pegar donde lo compraste — así de simple, sin registros técnicos.
          </p>
          <p className="text-[#0369A1]">
            Tranquilo: tu <strong>correo no se toca</strong>. Si ya usas este dominio para
            recibir correo, lo conservamos tal cual — solo tu página web pasa a mostrarse desde aquí.
          </p>
          {/* 2026-09-02 (Grupo 6 de la auditoría) — la casilla de «modo avanzado»
              (CNAME) se OCULTA. Enrutaba a casi todos —justo a quien ya usa el
              dominio para correo— a un camino que nunca recibió el tratamiento
              del resto (sin reintento, sin diagnóstico, errores de Cloudflare
              descartados) y ni siquiera cumplía su premisa: el modo por defecto
              YA preserva el correo (reimporta MX/TXT). Ocultarla apaga 6
              hallazgos sin construir nada. Terminar o borrar el modo CNAME es
              una decisión aparte. */}
        </div>
      )}

      {domains.map((d) => (
        <div key={d.id} className="border rounded-lg p-4 space-y-3 bg-white">
          <div className="flex justify-between items-start gap-2">
            <div>
              <p className="font-semibold">{d.hostname}</p>
              <p className="text-xs text-[#64748B]">
                Estado: <strong>{d.estado_humano || d.status}</strong>
                {d.ssl_label ? ` · ${d.ssl_label}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => verify(d.id)}>Verificar</Button>
              <Button size="sm" variant="outline" onClick={() => remove(d.id)}>Quitar</Button>
            </div>
          </div>

          {/* 2026-09-02 — POR QUE el dominio no aparece, con nombres.
              Antes esta tarjeta solo decia «awaiting_nameservers», igual si el
              dueño no habia hecho nada que si habia puesto los nameservers de
              OTRO dominio suyo — que es el error natural de quien ya conecto
              uno antes, y el que hace perder horas porque esta seguro de
              haberlo hecho bien. Pasó en vivo y costó media hora encontrarlo. */}
          {d.diagnostico_texto && (
            <div
              data-testid={`dominio-diagnostico-${d.id}`}
              className={
                d.diagnostico?.estado === 'bien_delegado'
                  ? 'text-sm rounded p-3 bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'text-sm rounded p-3 bg-amber-50 border border-amber-200 text-amber-900'
              }
            >
              <p className="font-medium mb-1">
                {d.diagnostico?.estado === 'bien_delegado'
                  ? 'Ya está bien configurado'
                  : d.diagnostico?.estado === 'mal_delegado'
                    ? 'Los datos que pusiste no son los de este dominio'
                    : 'Falta un paso donde compraste tu dominio'}
              </p>
              <p>{d.diagnostico_texto}</p>

              {/* Los dos lados, uno debajo del otro. Ver la diferencia con los
                  ojos vale mas que leerla en una frase. */}
              {d.diagnostico?.estado === 'mal_delegado' && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="rounded bg-white/70 p-2">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">
                      Tu dominio apunta hoy a
                    </p>
                    {(d.diagnostico.actuales || []).map((ns) => (
                      <code key={ns} className="block font-mono text-xs break-all line-through">{ns}</code>
                    ))}
                  </div>
                  <div className="rounded bg-white/70 p-2">
                    <p className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">
                      Tiene que apuntar a
                    </p>
                    {(d.diagnostico.esperados || []).map((ns) => (
                      <p key={ns} className="flex items-center gap-2 flex-wrap">
                        <code className="font-mono text-xs break-all">{ns}</code>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                                onClick={() => copy(ns)}>Copiar</Button>
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {d.diagnostico?.revisado_en && (
                <p className="text-[11px] mt-2 opacity-70">
                  Revisado el {new Date(d.diagnostico.revisado_en).toLocaleString('es-MX')}.
                  Pulsa «Verificar» para volver a mirar.
                </p>
              )}
            </div>
          )}
          {d.connection_mode === 'nameserver' && d.nameservers?.length > 0 && (
            <div className="bg-[#0F172A] text-white rounded p-3 text-sm space-y-2">
              <p className="text-[#94A3B8] text-xs">
                Pega estos {d.nameservers.length} datos donde compraste tu dominio, en la sección "Nameservers" o
                "Servidores de nombre" (reemplaza los que ya estén ahí):
              </p>
              {d.nameservers.map((ns) => (
                <p key={ns} className="flex items-center gap-2 flex-wrap">
                  <code className="break-all font-mono text-[#7DD3FC]">{ns}</code>
                  <Button size="sm" variant="outline" className="text-[#0F172A]" onClick={() => copy(ns)}>Copiar</Button>
                </p>
              ))}
            </div>
          )}
          {d.connection_mode === 'nameserver' && !d.nameservers?.length && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
              Aún no tenemos los nameservers de este dominio. Pulsa «Verificar» o vuelve a intentar el registro.
            </p>
          )}
          {d.connection_mode !== 'nameserver' && d.domain_type === 'custom' && d.dns_target && (
            <div className="bg-[#0F172A] text-white rounded p-3 text-sm space-y-1">
              <p className="text-[#94A3B8] text-xs">Sin importar dónde compraste tu dominio, crea un registro CNAME que apunte a:</p>
              <p className="flex items-center gap-2 flex-wrap">
                <code className="break-all font-mono text-[#7DD3FC]">{d.dns_target}</code>
                <Button size="sm" variant="outline" className="text-[#0F172A]" onClick={() => copy(d.dns_target)}>Copiar</Button>
              </p>
            </div>
          )}
          {d.connection_mode !== 'nameserver' && d.domain_type === 'custom' && !d.dns_target && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
              Aún no tenemos un valor de destino para este dominio. Pulsa «Verificar» o vuelve a intentar el registro.
            </p>
          )}
          {d.simple_guide?.length > 0 && (
            <ol className="list-decimal ml-5 text-sm space-y-2 text-[#334155]">
              {d.simple_guide.map((s) => (
                <li key={s.step}><strong>{s.title}</strong> — {s.detail}</li>
              ))}
            </ol>
          )}
          {d.apex_note && <p className="text-xs text-[#64748B]">{d.apex_note}</p>}
          {d.provider_guides?.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Guías por proveedor</summary>
              <div className="mt-2 space-y-2">
                {d.provider_guides.map((g) => (
                  <div key={g.id} className="bg-[#F8FAFC] p-2 rounded border">
                    <p className="font-semibold">{g.title}</p>
                    <pre className="whitespace-pre-wrap text-xs mt-1">{g.steps}</pre>
                  </div>
                ))}
              </div>
            </details>
          )}
          <details className="text-xs">
            <summary className="cursor-pointer text-[#64748B]">Información técnica</summary>
            {(d.technical?.verification_records || []).map((r, i) => (
              <div key={i} className="mt-2 bg-[#F8FAFC] p-2 rounded border">
                <p>Tipo: {r.type}</p>
                <p>Nombre: {r.name}</p>
                <p className="flex items-center gap-2 flex-wrap">
                  Valor: <code className="break-all">{r.value}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(r.value)}>Copiar</Button>
                </p>
              </div>
            ))}
            {d.connection_mode === 'nameserver' && (d.technical?.dns_import_snapshot || []).length > 0 && (
              <p className="mt-2 text-[#64748B]">
                Registros que encontramos y conservamos (correo, etc.): {(d.technical.dns_import_snapshot || []).map((r) => r.type).join(', ')}
              </p>
            )}
            {d.technical?.last_error && <p className="text-red-600 mt-1">{d.technical.last_error}</p>}
          </details>
        </div>
      ))}
    </div>
  );
}
